import LivingPhoto, { Pin, PhotoLegend } from "@/components/living/LivingPhoto";
import { useScrollStage } from "@/components/living/useScrollStage";

/* Image 2: the owner and the paperwork. Coordinates are image pixels (1024 x 768). */
const HUB = { x: 512, y: 150, on: 7, sub: "Captured" };

const EVENTS = [
  { id: "call", x: 560, y: 372, on: 1, src: "Phone", time: "11:18", body: <><b>Missed call</b> +91 98··· ···42</>, cap: "Lead created" },
  { id: "wa", x: 410, y: 545, on: 2, src: "WhatsApp", time: "11:21", body: <><b>Sunil Traders:</b> Bhai, quote bheja kya?</>, cap: "Quote task assigned" },
  { id: "sheet", x: 790, y: 440, on: 3, src: "Sheet", time: "11:24", body: <>Row 212 edited · <b>Payment due</b> 3 days</>, cap: "Reminder scheduled" },
  { id: "doc", x: 740, y: 690, on: 4, src: "Document", time: "11:30", body: <><b>Invoice #1187</b> scanned</>, cap: "Read and filed" },
  { id: "rem", x: 140, y: 465, on: 5, src: "Calendar", time: "11:32", body: <><b>Renewal</b> Mehta AMC expires Friday</>, cap: "Owner alerted" },
  { id: "task", x: 900, y: 585, on: 6, src: "Note", time: "11:35", body: <>Call back <b>Priya</b> re: site visit</>, cap: "Follow-up queued" },
];

const PATHS = EVENTS.map((e, i) => ({
  id: `c-${e.id}`,
  d: `M${e.x} ${e.y} L${HUB.x} ${HUB.y}`,
  on: 7,
  stream: 7,
  className: "lp__line--cobalt",
  dur: 2 + (i % 3) * 0.4,
  delay: i * 0.2,
}));

const FLIPS = [
  ["Scattered", "Connected", 7],
  ["Manual", "Automated", 8],
  ["Reactive", "Proactive", 9],
];

const WRAP = "mx-auto max-w-[1400px] px-5 md:px-8";

export default function BeforeScene() {
  const { ref, stage, inView, wrapperStyle } = useScrollStage(9);
  const captured = stage >= 7;

  return (
    <section ref={ref} className="scene border-b border-ink" style={wrapperStyle} data-testid="section-before">
      <div className={`scene__sticky ${WRAP} py-10 md:py-8`}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-center">
          <div className="lg:col-span-5">
            <p className="eyebrow">Human friction</p>
            <h2 className="display mt-5 text-[34px] md:text-[48px] leading-[0.98]">
              The business has grown. The systems have not caught up.
            </h2>
            <p className="mt-4 max-w-[40ch] text-lg">
              <b className="font-semibold text-ink">Nothing here is a mistake.</b>
              <span className="block mt-2 text-[15px] text-ink/65">
                It is one person holding the whole day in their head. Every new
                message adds to it.
              </span>
            </p>

            <dl className="mt-8 border-t border-ink">
              {FLIPS.map(([before, after, on]) => (
                <div key={before} className="flex items-baseline justify-between gap-4 py-3 border-b border-ink/20">
                  <dt className="eyebrow">{stage >= on ? "Now" : "Today"}</dt>
                  <dd className="flip display text-[30px] md:text-[40px] leading-none" data-on={stage >= on} aria-label={stage >= on ? after : before}>
                    <span aria-hidden="true">{before}</span>
                    <span aria-hidden="true">{after}</span>
                  </dd>
                </div>
              ))}
            </dl>
            <p className="mt-4 text-[13px] text-ink/60" aria-live="polite">
              {stage < 7 ? `${Math.min(stage, 6)} of 6 things arrived in the last twenty minutes.` : "Every one of them captured, routed and remembered."}
            </p>
          </div>

          <div className="lg:col-span-7">
            <LivingPhoto
              name="owner"
              alt="A business owner at a desk checking his phone, surrounded by paperwork, sticky notes, a calendar and a laptop spreadsheet"
              stage={stage}
              inView={inView}
              paths={PATHS}
              hub={HUB}
              className="border border-ink"
            >
              {EVENTS.map((e) => (
                <Pin key={e.id} name="owner" x={e.x} y={e.y} on={e.on} stage={stage}>
                  <div className={`notif ${captured ? "notif--captured" : ""}`}>
                    <div className="notif__bar"><span>{e.src}</span><time>{e.time}</time></div>
                    <div className="notif__body">{e.body}</div>
                    <div className="notif__cap">{e.cap}</div>
                  </div>
                </Pin>
              ))}
            </LivingPhoto>
            <PhotoLegend
              label="What arrived on the owner's desk"
              stage={stage}
              items={EVENTS.map((e) => ({
                id: e.id,
                on: e.on,
                done: 7,
                title: `${e.src} · ${e.time}`,
                sub: captured ? e.cap : e.body,
              }))}
            />
          </div>
        </div>
      </div>
    </section>
  );
}
